import { useMutation } from '@tanstack/react-query';
import { Eye, EyeOff } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';

import { IconButton } from '@doger/ui/components/ui/button';
import { $$toast } from '@doger/ui/components/ui/toast';
import { useAppForm } from '@doger/ui/form';
import { $$api } from '@doger/core/shared/api';

import { useAuthContext } from '../auth.context';
import styles from './reset-password-form.module.scss';

type ResetPasswordFormProps = {
  className?: string;
  token: string;
  onSuccess?: () => void;
};

export const ResetPasswordForm = ({
  className,
  token,
  onSuccess,
}: ResetPasswordFormProps) => {
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const { signInUrl } = useAuthContext();
  const { t } = useTranslation('core');

  const resetPasswordSchema = z
    .object({
      password: z
        .string()
        .min(6, t('validation.auth.passwordMinLength', { min: 6 })),
      confirmPassword: z
        .string()
        .min(1, t('validation.auth.confirmPasswordRequired')),
    })
    .refine((data) => data.password === data.confirmPassword, {
      message: t('validation.auth.passwordsMismatch'),
      path: ['confirmPassword'],
    });

  const resetPassword = useMutation({
    mutationFn: async ({ password }: z.infer<typeof resetPasswordSchema>) =>
      $$api.resetPassword({
        resetToken: token,
        newPassword: password,
      }),

    onSuccess: () => {
      $$toast.success(t('auth.resetPassword.successMessage'));
      navigate(signInUrl);
      onSuccess?.();
    },

    onError: () => {
      $$toast.error(t('auth.resetPassword.errorMessage'));
    },
  });

  const form = useAppForm({
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
    onSubmit: async ({ value }) => {
      await resetPassword.mutateAsync(value);
    },
    validators: {
      onSubmit: resetPasswordSchema,
      onChange: resetPasswordSchema,
    },
  });

  return (
    <form
      name='reset-password-form'
      className={className}
      onSubmit={(event) => {
        event.preventDefault();
        form.handleSubmit();
      }}
    >
      <div className={styles.fields}>
        <form.AppForm>
          <form.AppField
            name='password'
            children={(field) => (
              <field.TextField
                label={t('auth.resetPassword.newPasswordLabel')}
                variant='contained'
                type={showPassword ? 'text' : 'password'}
                placeholder={t('auth.resetPassword.newPasswordPlaceholder')}
                autoComplete='new-password'
                rightAdornment={
                  <IconButton
                    type='button'
                    variant='text'
                    size='md'
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff /> : <Eye />}
                  </IconButton>
                }
                data-clarity-mask='true'
              />
            )}
          />
        </form.AppForm>

        <form.AppForm>
          <form.AppField
            name='confirmPassword'
            children={(field) => (
              <field.TextField
                label={t('auth.resetPassword.confirmPasswordLabel')}
                variant='contained'
                type={showPassword ? 'text' : 'password'}
                placeholder={t('auth.resetPassword.confirmPasswordPlaceholder')}
                autoComplete='new-password'
                data-clarity-mask='true'
              />
            )}
          />
        </form.AppForm>
      </div>

      <form.AppForm>
        <form.SubmitButton
          variant='contained'
          size='lg'
          palette='primary'
          children={({ isSubmitting }) =>
            isSubmitting
              ? t('auth.resetPassword.submitting')
              : t('auth.resetPassword.submitButton')
          }
        />
      </form.AppForm>
    </form>
  );
};
